import { useSyncExternalStore } from "react";

/**
 * Stato del sub-tenant (ambiente) attivo, lato app DriveBy. Alimentato dal claim
 * `sub_tenants` del token (via OidcSessionBridge). Il sub-tenant selezionato è
 * inviato come header `X-Tenant` su ogni chiamata API (RestAPI). Persistito così
 * da sopravvivere al reload; ripulito al logout / cambio realm.
 *
 * Non sta in `lib/` perché il concetto di sub-tenant è specifico di DriveBy
 * (lib/ deve restare app-agnostico).
 */

const SELECTED_KEY = "mdb.subtenant";

/**
 * Le due risposte di `/me` che non sono errori transitori ma stati da cui si esce
 * solo cambiando ambiente: 409 dal filtro quando il nucleo attivo non è
 * determinabile, 404 quando il token è valido ma in QUESTO ambiente la persona non
 * ha un profilo. Ritentare non serve; scegliere un altro ambiente sì.
 */
export type SubTenantProblem = "SUBTENANT_REQUIRED" | "USER_NOT_FOUND";

type State = {
  available: string[];
  selected: string | null;
  /**
   * True mentre `/me` è in volo per il nucleo appena scelto. `/me` risponde coi
   * ruoli e privilegi del nucleo attivo, quindi fra la scelta e la risposta la
   * sessione porta ancora i permessi dell'ambiente precedente: chi naviga in
   * quella finestra vedrebbe menu e azioni sbagliati. Alzato da OidcSessionBridge.
   */
  syncing: boolean;
  /** Motivo terminale dell'ultima `/me`, se ce n'è uno. */
  problem: SubTenantProblem | null;
};

let state: State = {
  available: [],
  selected: window.localStorage.getItem(SELECTED_KEY),
  syncing: false,
  problem: null,
};

const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function persist(selected: string | null): void {
  if (selected) window.localStorage.setItem(SELECTED_KEY, selected);
  else window.localStorage.removeItem(SELECTED_KEY);
}

/** Sceglie il default fra i sub-tenant disponibili: 'prod' se presente, altrimenti il primo. */
function pickDefault(available: string[]): string | null {
  if (available.length === 0) return null;
  return available.includes("prod") ? "prod" : available[0];
}

/**
 * Aggiorna i sub-tenant disponibili (dal claim). Auto-seleziona quando c'è una
 * sola scelta, o quando la selezione persistita non è più valida; altrimenti
 * mantiene la selezione esistente. Idempotente.
 */
export function setAvailableSubTenants(available: string[]): void {
  const selected =
    state.selected && available.includes(state.selected) ? state.selected : pickDefault(available);
  state = { ...state, available, selected };
  persist(selected);
  emit();
}

export function setSelectedSubTenant(selected: string | null): void {
  if (selected !== null && !state.available.includes(selected)) return;
  // Cambiare ambiente è il rimedio al motivo terminale: sceglierne uno lo ritira,
  // altrimenti l'avviso resterebbe a schermo mentre il nuovo `/me` è in volo.
  state = { ...state, selected, problem: null };
  persist(selected);
  emit();
}

export function clearSubTenants(): void {
  state = { available: [], selected: null, syncing: false, problem: null };
  persist(null);
  emit();
}

/**
 * Registra il motivo terminale. NON tocca `available`: quell'elenco viene dal claim
 * del token, non da `/me`, ed è l'unica via d'uscita che resta all'utente.
 */
export function setSubTenantProblem(problem: SubTenantProblem): void {
  state = { ...state, problem };
  emit();
}

/** Alzato dal bridge quando parte un `/me` per un nucleo nuovo. */
export function beginSubTenantSync(): void {
  if (state.syncing) return;
  state = { ...state, syncing: true };
  emit();
}

/** Abbassato dal bridge quando quel `/me` si è concluso, riuscito o fallito. */
export function endSubTenantSync(): void {
  if (!state.syncing) return;
  state = { ...state, syncing: false };
  emit();
}

/** Letto dall'interceptor RestAPI per l'header X-Tenant. */
export function getSelectedSubTenant(): string | null {
  return state.selected;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): State {
  return state;
}

/** Lettura sincrona dello stato (guardie di rotta, test). */
export function getSubTenantSnapshot(): State {
  return state;
}

/** Hook UI: { available, selected }. */
export function useSubTenant(): State {
  return useSyncExternalStore(subscribe, getSnapshot);
}
